import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const PMSPayments = ({ userInfo }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonthNum, setSelectedMonthNum] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [pmsTransfers, setPmsTransfers] = useState({});
  const [pmsTransfersConfirmed, setPmsTransfersConfirmed] = useState({});
  const [categoryBreakdowns, setCategoryBreakdowns] = useState({});
  const [expandedGrantDetails, setExpandedGrantDetails] = useState({});
  const [expandedPmsDetails, setExpandedPmsDetails] = useState({});
  const [monthlyTransactionStatus, setMonthlyTransactionStatus] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [monthlyCycleSettings, setMonthlyCycleSettings] = useState({ cycleStartMonth: null });
  const [monthNotInCycle, setMonthNotInCycle] = useState(false);

  // Get available years (current year and past 2 years)
  const getAvailableYears = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    return [currentYear, currentYear - 1, currentYear - 2];
  };

  // Get month names
  const monthNames = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const availableYears = getAvailableYears();

  // Generate list of current month and past 12 months
  const getMonths = () => {
    const monthsList = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    for (let i = 0; i < 12; i++) {
      let monthIndex = currentMonth - i;
      let year = currentYear;

      if (monthIndex < 0) {
        monthIndex += 12;
        year -= 1;
      }

      const monthName = new Date(year, monthIndex, 1).toLocaleString('default', { month: 'long' });
      monthsList.push({
        label: `${monthName} ${year}`,
        value: `${year}-${(monthIndex + 1).toString().padStart(2, '0')}`
      });
    }

    return monthsList;
  };

  const months = getMonths();

  // Initialize selected month from URL params or default to current month
  useEffect(() => {
    const monthParam = searchParams.get('month');
    if (monthParam) {
      const isValidFormat = /^\d{4}-\d{2}$/.test(monthParam);
      if (isValidFormat) {
        setSelectedMonth(monthParam);
        const [year, monthNum] = monthParam.split('-');
        setSelectedYear(year);
        setSelectedMonthNum(monthNum);
      } else if (months.length > 0) {
        setSelectedMonth(months[0].value);
        const [year, monthNum] = months[0].value.split('-');
        setSelectedYear(year);
        setSelectedMonthNum(monthNum);
      }
    } else if (months.length > 0) {
      // Default to current month
      setSelectedMonth(months[0].value);
      const [year, monthNum] = months[0].value.split('-');
      setSelectedYear(year);
      setSelectedMonthNum(monthNum);
    }
  }, []);

  // Handle year dropdown change
  const handleYearChange = (year) => {
    setSelectedYear(year);
    const newMonth = `${year}-${selectedMonthNum}`;
    setSelectedMonth(newMonth);
    setSearchParams({ month: newMonth });
  };

  // Handle month dropdown change
  const handleMonthNumChange = (monthNum) => {
    setSelectedMonthNum(monthNum);
    const newMonth = `${selectedYear}-${monthNum}`;
    setSelectedMonth(newMonth);
    setSearchParams({ month: newMonth });
  };

  // Get display label for selected month
  const getMonthLabel = (monthYear) => {
    if (!monthYear) return '';
    const [year, monthNum] = monthYear.split('-');
    const monthName = monthNames.find(m => m.value === monthNum)?.label || '';
    return `${monthName} ${year}`;
  };

  useEffect(() => {
    fetchAllData();
    fetchMonthlyCycleSettings();
  }, []);

  // Check if selected month is within cycle range
  useEffect(() => {
    if (selectedMonth && monthlyCycleSettings.cycleStartMonth) {
      const isInCycle = months.some(m => m.value === selectedMonth);
      // Also check if month is >= cycle start month
      const isAfterCycleStart = selectedMonth >= monthlyCycleSettings.cycleStartMonth;
      setMonthNotInCycle(!isInCycle || !isAfterCycleStart);
    } else if (selectedMonth && !monthlyCycleSettings.cycleStartMonth) {
      // No cycle started
      setMonthNotInCycle(true);
    }
  }, [selectedMonth, monthlyCycleSettings.cycleStartMonth, months]);

  const fetchMonthlyCycleSettings = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/month-status/monthly-cycle-settings`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setMonthlyCycleSettings(data);
      }
    } catch (error) {
      console.error('Error fetching monthly cycle settings:', error);
    }
  };

  const fetchAllData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchAllMonthsTransactionStatus(),
        fetchPmsTransferConfirmations()
      ]);
    } catch (error) {
      console.error('Error fetching PMS data:', error);
      setErrorMessage('Failed to load PMS payment data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllMonthsTransactionStatus = async () => {
    try {
      const statusByMonth = {};

      await Promise.all(months.map(async (month) => {
        const [year, monthNum] = month.value.split('-');

        statusByMonth[month.value] = {
          hasTransactions: false,
          hasUnconfirmed: false,
          allConfirmed: false,
          unconfirmedCount: 0,
          totalCount: 0,
          types: {
            'debit': { total: 0, unconfirmed: 0 },
            'credit': { total: 0, unconfirmed: 0 },
          },
          label: month.label
        };

        // Fetch month status from the expense endpoint which gives us hasTransactions, hasUnconfirmed, allConfirmed
        try {
          const monthStatusResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/expense/month-status?year=${year}&month=${monthNum}`, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          });

          if (monthStatusResponse.ok) {
            const monthStatusData = await monthStatusResponse.json();
            console.log(`Month status for ${month.label}:`, monthStatusData);
            if (monthStatusData.hasTransactions) {
              statusByMonth[month.value].hasTransactions = true;
            }
            statusByMonth[month.value].totalCount += monthStatusData.totalCount || 0;
            statusByMonth[month.value].types['debit'].total = monthStatusData.totalCount || 0;
            statusByMonth[month.value].types['debit'].unconfirmed = monthStatusData.unconfirmedCount || 0;
            if (monthStatusData.unconfirmedCount > 0) {
              statusByMonth[month.value].hasUnconfirmed = true;
              statusByMonth[month.value].unconfirmedCount += monthStatusData.unconfirmedCount;
            }
          }
        } catch (error) {
          console.error(`Error fetching month status for ${month.label}:`, error);
        }

        // Also fetch credit card month status
        try {
          const creditStatusResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/credit-card/expense/unconfirmed?year=${year}&month=${monthNum}`, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          });

          if (creditStatusResponse.ok) {
            const creditData = await creditStatusResponse.json();
            const unconfirmedCount = creditData.transactions?.length || 0;
            statusByMonth[month.value].types['credit'].unconfirmed = unconfirmedCount;
            if (unconfirmedCount > 0) {
              statusByMonth[month.value].hasUnconfirmed = true;
              statusByMonth[month.value].unconfirmedCount += unconfirmedCount;
            }
          }
        } catch (error) {
          console.error(`Error fetching credit card status for ${month.label}:`, error);
        }

        // Determine if all transactions are confirmed
        statusByMonth[month.value].allConfirmed =
          statusByMonth[month.value].hasTransactions &&
          !statusByMonth[month.value].hasUnconfirmed;

        console.log(`Final status for ${month.label}:`, statusByMonth[month.value]);

        // Fetch PMS transfers for this month if all transactions are confirmed
        if (statusByMonth[month.value].allConfirmed) {
          await fetchPmsTransfersForMonth(month.value);
        }
      }));

      setMonthlyTransactionStatus(statusByMonth);
    } catch (error) {
      console.error('Error fetching transaction status:', error);
    }
  };

  const fetchPmsTransfersForMonth = async (monthYear) => {
    try {
      const [year, monthNum] = monthYear.split('-');

      // Fetch confirmed transactions to calculate PMS transfers
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/expense/confirmed?year=${year}&month=${monthNum}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) {
        return;
      }

      const data = await response.json();
      console.log("Confirmed transactions data for PMS calculation:", data);
      const transactions = data.transactions || [];

      // Calculate PMS transfers by grant using defaultPayment allocations
      const grantTotals = {};
      const grantCategoryBreakdowns = {};

      transactions.forEach(transaction => {
        const totalAmount = Math.abs(parseFloat(transaction.amount) || 0);
        const category = transaction.category || 'Uncategorized';
        
        // Parse defaultPayment to get grant allocations (percentages)
        let grantAllocations = {};
        try {
          if (transaction.defaultPayment) {
            grantAllocations = typeof transaction.defaultPayment === 'string' 
              ? JSON.parse(transaction.defaultPayment) 
              : transaction.defaultPayment;
          }
        } catch (e) {
          console.error('Error parsing defaultPayment:', e);
        }

        // If no allocations found, assign to non-grant funds
        if (Object.keys(grantAllocations).length === 0) {
          grantAllocations = { 'non-grant funds': '100' };
        }

        // Distribute the transaction amount across grants based on percentages
        Object.entries(grantAllocations).forEach(([grant, percentage]) => {
          const pct = parseFloat(percentage) || 0;
          if (pct === 0) return; // Skip 0% allocations
          
          const grantAmount = (totalAmount * pct) / 100;
          const grantKey = grant === 'non-grant funds' ? 'non-grant funds' : grant;

          if (!grantTotals[grantKey]) {
            grantTotals[grantKey] = 0;
            grantCategoryBreakdowns[grantKey] = {};
          }

          grantTotals[grantKey] += grantAmount;

          if (!grantCategoryBreakdowns[grantKey][category]) {
            grantCategoryBreakdowns[grantKey][category] = 0;
          }
          grantCategoryBreakdowns[grantKey][category] += grantAmount;
        });
      });

      console.log("grantcategorybreakdowns:", grantCategoryBreakdowns);

      setPmsTransfers(prev => ({
        ...prev,
        [monthYear]: grantTotals
      }));

      Object.keys(grantTotals).forEach(grant => {
        setCategoryBreakdowns(prev => ({
          ...prev,
          [`${monthYear}-${grant}`]: grantCategoryBreakdowns[grant] || {}
        }));
      });
    } catch (error) {
      console.error(`Error fetching PMS transfers for ${monthYear}:`, error);
    }
  };

  const fetchPmsTransferConfirmations = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/pms-transfers/status`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        setPmsTransfersConfirmed(data.confirmedMonths || {});
      }
    } catch (error) {
      console.error('Error fetching PMS transfer confirmations:', error);
    }
  };

  const handleConfirmPmsTransfers = async (monthYear) => {
    try {
      const [year, month] = monthYear.split('-');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/pms-transfers/confirm`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          year,
          month,
          transfers: pmsTransfers[monthYear] || {},
          confirmed: true
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      setPmsTransfersConfirmed(prev => ({
        ...prev,
        [monthYear]: true
      }));

      await fetchAllMonthsTransactionStatus();
      setSuccessMessage('PMS statement sent successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error sending PMS statement:', error);
      setErrorMessage('Failed to send PMS statement. Please try again.');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleUnconfirmPmsTransfers = async (monthYear) => {
    if (!window.confirm('Are you sure you want to undo the PMS transfer confirmation?')) {
      return;
    }

    try {
      const [year, month] = monthYear.split('-');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/pms-transfers/confirm`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year, month })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      setPmsTransfersConfirmed(prev => {
        const updated = { ...prev };
        delete updated[monthYear];
        return updated;
      });

      await fetchAllMonthsTransactionStatus();
      setSuccessMessage('PMS transfer confirmation has been undone.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error unconfirming PMS transfers:', error);
      setErrorMessage('Failed to undo PMS transfer confirmation. Please try again.');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const calculateTotalPmsTransfer = (transfers) => {
    if (!transfers || typeof transfers !== 'object') return 0;

    return Object.entries(transfers)
      .filter(([grant]) => grant.toLowerCase() !== "non-grant funds")
      .reduce((total, [, amount]) => total + parseFloat(amount || 0), 0);
  };

  console.log("monthly transaction status:", monthlyTransactionStatus);

  // Get status for selected month
  const selectedMonthStatus = monthlyTransactionStatus[selectedMonth];

  // Determine the current status of the selected month
  const getSelectedMonthState = () => {
    if (!selectedMonthStatus) {
      return 'no-data';
    }
    if (!selectedMonthStatus.hasTransactions) {
      return 'waiting-upload';
    }
    if (selectedMonthStatus.hasUnconfirmed) {
      return 'waiting-confirmation';
    }
    if (selectedMonthStatus.allConfirmed && !pmsTransfersConfirmed[selectedMonth]) {
      return 'ready-for-pms';
    }
    if (pmsTransfersConfirmed[selectedMonth]) {
      return 'completed';
    }
    return 'unknown';
  };

  const selectedMonthState = getSelectedMonthState();

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="card p-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-3 text-gray-600">Loading PMS payments...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="card p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">PMS Payments</h1>
        <p className="text-gray-600">Manage and confirm monthly PMS transfers to grants</p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {errorMessage}
        </div>
      )}

      {/* Month Selector */}
      <div className="card p-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => handleYearChange(e.target.value)}
              className="w-32 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
            <select
              value={selectedMonthNum}
              onChange={(e) => handleMonthNumChange(e.target.value)}
              className="w-40 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              {monthNames.map(month => (
                <option key={month.value} value={month.value}>{month.label}</option>
              ))}
            </select>
          </div>
          <div className="ml-auto">
            <span className="text-lg font-semibold text-gray-900">{getMonthLabel(selectedMonth)}</span>
          </div>
        </div>
      </div>

      {/* Month Not In Cycle Warning */}
      {monthNotInCycle && (
        <div className="card p-6 border-amber-200 bg-amber-50">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h3 className="font-medium text-amber-800">Month Outside Monthly Cycle</h3>
              <p className="text-sm text-amber-700 mt-1">
                {!monthlyCycleSettings.cycleStartMonth 
                  ? "Monthly tasks cycle has not been started yet. Please start the cycle from the Admin Todolist page."
                  : `The selected month (${getMonthLabel(selectedMonth)}) is outside the current monthly tasks cycle which started in ${getMonthLabel(monthlyCycleSettings.cycleStartMonth)}.`
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Selected Month Status Card - Only show if month is in cycle */}
      {!monthNotInCycle && (
      <div className="card p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          PMS Status: {getMonthLabel(selectedMonth)}
        </h2>

        {/* Waiting for Statement Upload */}
        {selectedMonthState === 'waiting-upload' && (
          <div className="border border-gray-200 rounded-lg bg-gray-50 p-6">
            <div className="flex items-center justify-center flex-col py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Waiting for Statement Upload</h3>
              <p className="text-gray-600 text-center mb-4">
                Bank statements need to be uploaded before PMS transfers can be processed.
              </p>
              <button
                className="btn-primary"
                onClick={() => navigate(`/grant-fund-financials?section=financials-import&month=${selectedMonth}`)}
              >
                Upload Statements
              </button>
            </div>
          </div>
        )}

        {/* Waiting for Client Confirmation */}
        {selectedMonthState === 'waiting-confirmation' && (
          <div className="border border-blue-200 rounded-lg bg-blue-50 p-6">
            <div className="flex items-center justify-center flex-col py-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Waiting for Transaction Confirmation</h3>
              <p className="text-gray-600 text-center mb-2">
                Client needs to confirm {selectedMonthStatus?.unconfirmedCount || 0} transaction{selectedMonthStatus?.unconfirmedCount !== 1 ? 's' : ''}.
              </p>
              <p className="text-sm text-gray-500 text-center mb-4">
                PMS transfers will be available once all transactions are confirmed.
              </p>
              <button
                className="btn-secondary"
                onClick={() => navigate(`/grant-fund-financials?tab=unconfirmed&year=${selectedMonth.split('-')[0]}&month=${selectedMonth.split('-')[1]}`)}
              >
                View Transactions
              </button>
            </div>
          </div>
        )}

        {/* Ready for PMS Transfer */}
        {selectedMonthState === 'ready-for-pms' && (
          <div className="border border-amber-200 rounded-lg bg-amber-50 p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Financial Allocations: {getMonthLabel(selectedMonth)}
                </h3>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 mt-1">
                  PMS Transfer Needed
                </span>
              </div>
            </div>

            {/* PMS Transfers Section */}
            <div className="bg-white rounded-lg border border-amber-200 p-4">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => setExpandedPmsDetails(prev => ({
                  ...prev,
                  [selectedMonth]: !prev[selectedMonth]
                }))}
              >
                <h4 className="font-medium text-gray-900">PMS Transfers Required</h4>
                <span className="text-gray-500">
                  {expandedPmsDetails[selectedMonth] ? '▼' : '▶'}
                </span>
              </div>

              {expandedPmsDetails[selectedMonth] && (
                <div className="mt-4">
                  {Object.keys(pmsTransfers[selectedMonth] || {}).length === 0 ? (
                    <p className="text-gray-500 italic">No PMS transfers required for this month.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Grant</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {Object.entries(pmsTransfers[selectedMonth] || {})
                            .filter(([grant]) => grant !== "non-grant funds")
                            .map(([grant, amount]) => {
                              const isGrantExpanded = expandedGrantDetails[`${selectedMonth}-${grant}`] || false;

                              return (
                                <React.Fragment key={grant}>
                                  <tr>
                                    <td className="px-4 py-3 text-sm text-gray-900">{grant}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(amount)}</td>
                                    <td className="px-4 py-3">
                                      <button
                                        className="text-sm text-indigo-600 hover:text-indigo-800"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedGrantDetails(prev => ({
                                            ...prev,
                                            [`${selectedMonth}-${grant}`]: !prev[`${selectedMonth}-${grant}`]
                                          }));
                                        }}
                                      >
                                        {isGrantExpanded ? 'Hide Details' : 'Show Details'}
                                      </button>
                                    </td>
                                  </tr>
                                  {isGrantExpanded && (
                                    <tr className="bg-gray-50">
                                      <td colSpan="3" className="px-4 py-3">
                                        <div className="pl-4">
                                          <h6 className="text-xs font-medium text-gray-700 mb-2">Spending by Category</h6>
                                          {!categoryBreakdowns[`${selectedMonth}-${grant}`] ? (
                                            <p className="text-sm text-gray-500 italic">No details available</p>
                                          ) : Object.keys(categoryBreakdowns[`${selectedMonth}-${grant}`]).length === 0 ? (
                                            <p className="text-sm text-gray-500 italic">No category data available</p>
                                          ) : (
                                            <table className="min-w-full">
                                              <tbody>
                                                {Object.entries(categoryBreakdowns[`${selectedMonth}-${grant}`]).map(([category, categoryAmount]) => (
                                                  <tr key={category}>
                                                    <td className="py-1 text-sm text-gray-600">{category}</td>
                                                    <td className="py-1 text-sm text-gray-900 text-right">{formatCurrency(categoryAmount)}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          <tr className="bg-gray-100 font-semibold">
                            <td className="px-4 py-3 text-sm text-gray-900">TOTAL</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(calculateTotalPmsTransfer(pmsTransfers[selectedMonth]))}</td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 flex justify-between items-center">
                <button
                  className="btn-primary"
                  onClick={() => handleConfirmPmsTransfers(selectedMonth)}
                >
                  Confirm PMS Transfers
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => navigate(`/grant-fund-financials?tab=confirmed&year=${selectedMonth.split('-')[0]}&month=${selectedMonth.split('-')[1]}`)}
                >
                  View Month
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Completed */}
        {selectedMonthState === 'completed' && (
          <div className="border border-green-200 rounded-lg bg-green-50 p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Financial Allocations: {getMonthLabel(selectedMonth)}
                </h3>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  PMS Transfers Completed
                </span>
              </div>
            </div>

            {/* PMS Transfers Section */}
            <div className="bg-white rounded-lg border border-green-200 p-4">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => setExpandedPmsDetails(prev => ({
                  ...prev,
                  [selectedMonth]: !prev[selectedMonth]
                }))}
              >
                <h4 className="font-medium text-gray-900">PMS Transfers Completed</h4>
                <span className="text-gray-500">
                  {expandedPmsDetails[selectedMonth] ? '▼' : '▶'}
                </span>
              </div>

              {expandedPmsDetails[selectedMonth] && (
                <div className="mt-4">
                  {Object.keys(pmsTransfers[selectedMonth] || {}).length === 0 ? (
                    <p className="text-gray-500 italic">No PMS transfers were made for this month.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Grant</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {Object.entries(pmsTransfers[selectedMonth] || {})
                            .filter(([grant]) => grant !== "non-grant funds")
                            .map(([grant, amount]) => {
                              const isGrantExpanded = expandedGrantDetails[`${selectedMonth}-${grant}`] || false;

                              return (
                                <React.Fragment key={grant}>
                                  <tr>
                                    <td className="px-4 py-3 text-sm text-gray-900">{grant}</td>
                                    <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(amount)}</td>
                                    <td className="px-4 py-3">
                                      <button
                                        className="text-sm text-indigo-600 hover:text-indigo-800"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedGrantDetails(prev => ({
                                            ...prev,
                                            [`${selectedMonth}-${grant}`]: !prev[`${selectedMonth}-${grant}`]
                                          }));
                                        }}
                                      >
                                        {isGrantExpanded ? 'Hide Details' : 'Show Details'}
                                      </button>
                                    </td>
                                  </tr>
                                  {isGrantExpanded && (
                                    <tr className="bg-gray-50">
                                      <td colSpan="3" className="px-4 py-3">
                                        <div className="pl-4">
                                          <h6 className="text-xs font-medium text-gray-700 mb-2">Spending by Category</h6>
                                          {!categoryBreakdowns[`${selectedMonth}-${grant}`] ? (
                                            <p className="text-sm text-gray-500 italic">No details available</p>
                                          ) : Object.keys(categoryBreakdowns[`${selectedMonth}-${grant}`]).length === 0 ? (
                                            <p className="text-sm text-gray-500 italic">No category data available</p>
                                          ) : (
                                            <table className="min-w-full">
                                              <tbody>
                                                {Object.entries(categoryBreakdowns[`${selectedMonth}-${grant}`]).map(([category, categoryAmount]) => (
                                                  <tr key={category}>
                                                    <td className="py-1 text-sm text-gray-600">{category}</td>
                                                    <td className="py-1 text-sm text-gray-900 text-right">{formatCurrency(categoryAmount)}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          <tr className="bg-gray-100 font-semibold">
                            <td className="px-4 py-3 text-sm text-gray-900">TOTAL</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(calculateTotalPmsTransfer(pmsTransfers[selectedMonth]))}</td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-4 flex justify-between items-center">
                <div className="flex items-center text-green-700">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                  </svg>
                  <span className="text-sm font-medium">PMS transfers confirmed</span>
                </div>
                <div className="flex gap-2">
                  <button
                    className="text-sm px-3 py-1 text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100"
                    onClick={() => handleUnconfirmPmsTransfers(selectedMonth)}
                  >
                    Undo Confirmation
                  </button>
                  <button
                    className="btn-secondary text-sm"
                    onClick={() => navigate(`/grant-fund-financials?tab=confirmed&year=${selectedMonth.split('-')[0]}&month=${selectedMonth.split('-')[1]}`)}
                  >
                    View Month
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No Data */}
        {(selectedMonthState === 'no-data' || selectedMonthState === 'unknown') && (
          <div className="border border-gray-200 rounded-lg bg-gray-50 p-6">
            <div className="flex items-center justify-center flex-col py-8">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
              <p className="text-gray-600 text-center mb-4">
                No financial data found for {getMonthLabel(selectedMonth)}.
              </p>
              <button
                className="btn-secondary"
                onClick={() => navigate(`/grant-fund-financials?section=financials-import&month=${selectedMonth}`)}
              >
                Go to Financials Import
              </button>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default PMSPayments;
